import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ErrorBanner,
  Input,
  Pill,
  SmartTable,
  WorkbenchPanel,
  type SmartColumn,
} from '@frm/ui';

import {
  enrollTelegramCandidate,
  fetchTelegramCandidates,
  fetchTelegramVerification,
  type TelegramVerification,
  type FrmEnrollmentCandidate,
  type EnrollmentResponse,
} from '@/api/telegramEnrollment';

const ERROR_LABELS: Record<string, string> = {
  telegram_unbound: 'Нет личной привязки Telegram',
  role_unsupported: 'Роль ФРМ не даёт доступ к ai.agents',
  user_banned: 'Пользователь заблокирован в ФРМ',
  user_deleted: 'Пользователь удалён в ФРМ',
  profile_missing: 'В ФРМ нет профиля',
  upstream_unavailable: 'ФРМ временно недоступен',
  temporarily_unavailable: 'Membership временно недоступен',
};

function errorLabel(error: unknown): string {
  const code = error instanceof Error ? error.message : 'unknown';
  return ERROR_LABELS[code] ?? 'Не удалось выдать доступ';
}

export default function TelegramEnrollmentPanel() {
  const [verification, setVerification] = useState<TelegramVerification[]>([]);
  const [verificationError, setVerificationError] = useState(false);
  const [rows, setRows] = useState<FrmEnrollmentCandidate[]>([]);
  const [tenantId, setTenantId] = useState('tenant_zaoblako');
  const [botId, setBotId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, EnrollmentResponse | string>>({});

  const load = () => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    setVerificationError(false);
    void fetchTelegramVerification(controller.signal)
      .then((result) => setVerification(result.bindings))
      .catch(() => { setVerification([]); setVerificationError(true); });
    void fetchTelegramCandidates(controller.signal)
      .then((response) => {
        setRows(response.users);
        setTenantId(response.tenantId);
      })
      .catch((error) => setLoadError(errorLabel(error)))
      .finally(() => setLoading(false));
    return () => controller.abort();
  };

  useEffect(load, []);

  const enroll = async (candidate: FrmEnrollmentCandidate) => {
    const normalizedBotId = botId.trim();
    if (!normalizedBotId || pendingUserId) return;
    setPendingUserId(candidate.id);
    setResults((current) => {
      const next = { ...current };
      delete next[candidate.id];
      return next;
    });
    try {
      const result = await enrollTelegramCandidate(candidate.id, normalizedBotId);
      setResults((current) => ({ ...current, [candidate.id]: result }));
    } catch (error) {
      setResults((current) => ({ ...current, [candidate.id]: errorLabel(error) }));
    } finally {
      setPendingUserId(null);
    }
  };

  const columns = useMemo<SmartColumn<FrmEnrollmentCandidate>[]>(() => [
    {
      key: 'displayName',
      header: 'Пользователь ФРМ',
      searchable: true,
      render: (row) => row.displayName ?? `ID ${row.id}`,
    },
    {
      key: 'id',
      header: 'FRM user ID',
      searchable: true,
      render: (row) => <code>{row.id}</code>,
    },
    {
      key: 'telegram',
      header: 'Telegram',
      render: (row) => {
        const result = results[row.id];
        if (!result) return <Pill tone="neutral">Проверка при добавлении</Pill>;
        if (typeof result === 'string') return <Pill tone="danger">{result}</Pill>;
        return <Pill tone="success">Доступ выдан · {result.role}</Pill>;
      },
    },
  ], [results]);

  return (
    <section className="telegram-enrollment" aria-labelledby="telegram-enrollment-title">
      <WorkbenchPanel
        eyebrow="ai.agents · TG-01"
        title={<span id="telegram-enrollment-title">Доступ к Telegram-боту</span>}
        description={`Тенант ${tenantId}. Telegram ID берётся только из личного профиля ФРМ.`}
        variant="raised"
      >
        <div className="telegram-enrollment__controls">
          <Input
            label="Bot registration ID"
            value={botId}
            onChange={(event) => setBotId(event.currentTarget.value)}
            placeholder="ai-agents-bot"
            maxLength={256}
          />
          <p>Ручной ввод Telegram ID запрещён. Без личной привязки ФРМ доступ не выдаётся.</p>
        </div>
        {loadError ? (
          <ErrorBanner tone="danger" title="Не удалось загрузить пользователей" description={loadError} />
        ) : null}
        {verificationError ? <ErrorBanner tone="danger" title="Не удалось загрузить состояние проверок" /> : null}
        <SmartTable
          ariaLabel="Состояние привязок Telegram"
          rows={verification}
          rowKey={(row) => row.bindingId}
          onRefresh={() => load()}
          columns={[
            { key: 'bindingId', header: 'Привязка', render: (row) => row.bindingId },
            { key: 'state', header: 'Состояние', render: (row) => ({
              verified: 'Проверено', grace_active: 'Временная ошибка — доступ сохранён',
              grace_expired: '72 часа истекли — доступ закрыт', revoked: 'Доступ отозван',
              configuration_error: 'Ошибка настройки проверки',
            }[row.state]) },
            { key: 'sourceCheckedAt', header: 'Успешная проверка (UTC)', render: (row) => row.sourceCheckedAt ?? '—' },
            { key: 'lastAttemptAt', header: 'Последняя попытка (UTC)', render: (row) => row.lastAttemptAt ?? '—' },
            { key: 'lastAttemptResult', header: 'Результат попытки', render: (row) => row.lastAttemptResult ?? '—' },
            { key: 'remainingMs', header: 'Осталось на момент обновления', render: (row) => `${Math.floor(row.remainingMs / 60000)} мин` },
          ]}
          emptyTitle="Привязок нет"
        />
        <SmartTable
          ariaLabel="Внутренние пользователи ФРМ"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={loading}
          searchPlaceholder="Найти по имени или ID"
          emptyTitle="Пользователи не найдены"
          onRefresh={() => load()}
          rowActions={(row) => (
            <Button
              size="sm"
              variant="primary"
              disabled={!botId.trim() || pendingUserId !== null}
              loading={pendingUserId === row.id}
              onClick={() => void enroll(row)}
            >
              Добавить
            </Button>
          )}
        />
      </WorkbenchPanel>
    </section>
  );
}
